import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../../../hooks/ReduxHook';
import {
  useCompleteSignUpMutation,
  useSignInByEmailMutation,
  useSignUpByEmailMutation,
  useVerifySignUpOtpMutation,
  useGetSignUpConfigQuery,
  useLazyGetSignUpConfigQuery,
  useResetPasswordMutation,
  useForgotPasswordMutation,
  useLazySwitchRoleQuery,
  useRespondNewPasswordChallengeMutation,
  useLazyReadMeQuery,
} from './Auth.api';
import {
  setToken,
  setUser,
  setRole,
  setSignUpFormPayload,
  setAccountType,
  setSubAccountType,
  setForgetPasswordFormPayload,
  setSetupMarker,
} from './Auth.reducer';
import {
  AUTH_LOCAL_COOKIE_KEY,
  AUTH_LOCAL_STORAGE_KEY,
  FORCE_CHANGE_PASSWORD_PATH,
  FORGET_PASSWORD_PATH,
  RESET_PASSWORD_PATH,
  SIGN_IN_PATH,
  SIGN_UP_ACCOUNT_TYPE_PATH,
  SIGN_UP_COMPLETE_PATH,
  SIGN_UP_PATH,
  SIGN_UP_VERIFICATION_PATH,
  CHALLENGE_SESSION_KEY,
  CHALLENGE_USERNAME_KEY,
  CHALLENGE_EMAIL_KEY,
} from './constants';
import {
  AccountType,
  CompleteSignUpPayload,
  ForgotPasswordPayload,
  ResetPasswordPayload,
  SignInByEmailPayload,
  SignUpByEmailPayload,
  SignUpByEmailResponse,
  SubAccountType,
  User,
  VerifySignUpOtpResponse,
} from './Auth.types';
import { useFormErrors } from '../../../../hooks/useFormErrors';
import { getQueryStringOfUrl, removeCookie, windowRedirect } from '../../../../util/helpers';
import useNavigation from '../../../../hooks/useNavigation';
import { useNotification } from '../../../../hooks/useNotification';
import { useAccountSwitcher } from '../../Users/subcomponents/AccountSwitcher/logic/useAccountSwitcher';
import { getSetupMarker, isHttpUrl, readUrlCallback, savePendingCallback, takePendingCallback } from './accountSetupStorage';
import { releaseCompletionGuard, submitCompleteSignUp, takeCompletionGuard } from './completeSignUpFlow';

const ACCOUNT_NOT_LOADED_MESSAGE = "We couldn't load your account. Try again.";
const ACCOUNT_NOT_CONFIRMED_MESSAGE = "We couldn't confirm the new account. Try again.";
const ADDITIONAL_ACCOUNT_CREATED_MESSAGE = 'The account has been added successfully.';

export const useAuthLogic = () => {
  const state = useAppSelector((state) => state.auth);
  const { gotoPage, currentPath } = useNavigation();
  const dispatch = useAppDispatch();
  const [errors, errorsMeta] = useFormErrors();
  const notification = useNotification();
  if (
    state.email === '' &&
    (currentPath === SIGN_UP_VERIFICATION_PATH ||
      currentPath === SIGN_UP_COMPLETE_PATH ||
      currentPath === SIGN_UP_ACCOUNT_TYPE_PATH)
  ) {
    gotoPage(SIGN_UP_PATH);
  }
  if (state.email === '' && currentPath === RESET_PASSWORD_PATH) {
    gotoPage(SIGN_IN_PATH);
  }

  useEffect(() => {
    if (currentPath === SIGN_IN_PATH) {
      sessionStorage.removeItem(CHALLENGE_SESSION_KEY);
      sessionStorage.removeItem(CHALLENGE_USERNAME_KEY);
      sessionStorage.removeItem(CHALLENGE_EMAIL_KEY);
    }
  }, [currentPath]);
  // APIs
  const [callSignInApi, { isLoading: isLoadingSignIn }] = useSignInByEmailMutation();
  const [callSignUpApi, { isLoading: isSignUpByEmailLoading }] = useSignUpByEmailMutation();
  const [doSwitchRole, { isLoading: isGetRoleLoading }] = useLazySwitchRoleQuery();
  const [callVerifySignUpOTPApi, { isLoading: isVerifySignUpOtpLoading }] = useVerifySignUpOtpMutation();
  const [callSignupConfigApi, { isLoading: isLoadingSignupConfig }] = useLazyGetSignUpConfigQuery();
  const [callCompleteSignUpApi, { isLoading: isLoadingCompleteSignup }] = useCompleteSignUpMutation();
  const [callResetPasswordApi, { isLoading: isLoadingResetPassword }] = useResetPasswordMutation();
  const [callForgetPasswordApi, { isLoading: isLoadingForgetPassword }] = useForgotPasswordMutation();
  const [callRespondChallengeApi, { isLoading: isLoadingRespondChallenge }] = useRespondNewPasswordChallengeMutation();
  const [callReadMeApi, { isFetching: isReadingMe }] = useLazyReadMeQuery();
  // Form Info
  const getFields = () => {
    const accountType = state.accountType as AccountType;
    if (!state.forms || !accountType) return [];
    const form = state.forms[accountType];
    if (state.subAccountType !== undefined) {
      return form?.types?.find((item) => item.value === state.subAccountType)?.fields || [];
    }
    return form?.fields || [];
  };

  const formInfo = {
    forms: state.forms,
    selectedForm: '',
    getFields,
    getTypes: () => state.forms?.[state.accountType as AccountType]?.types,
    accountType: state.accountType,
    subAccountType: state.subAccountType,
  };

  // Helper for API calls
  const handleApiCall = async (
    apiCall: () => Promise<any>,
    onSuccess: (response: any) => void,
    onError?: (response: any) => void,
  ) => {
    errorsMeta.clearErrors();
    try {
      const response = await apiCall();
      onSuccess(response);
    } catch (error) {
      errorsMeta.handleApiError(error);
      if (onError) {
        onError(error);
      }
    }
  };

  // Actions
  const doSignInByEmail = (signInParameter: SignInByEmailPayload) =>
    handleApiCall(
      () => callSignInApi(signInParameter).unwrap(),
      (data) => {
        if (data.challenge === 'NEW_PASSWORD_REQUIRED') {
          sessionStorage.setItem(CHALLENGE_SESSION_KEY, data.session || '');
          sessionStorage.setItem(CHALLENGE_USERNAME_KEY, data.username || '');
          sessionStorage.setItem(CHALLENGE_EMAIL_KEY, data.email || signInParameter.email || '');
          gotoPage(FORCE_CHANGE_PASSWORD_PATH);
          return;
        }
        if (data.challenge) {
          notification.error({
            message: 'Unsupported Login Challenge',
            description: `The system requested an unsupported challenge (${data.challenge}). Please contact support.`,
          });
          return;
        }
        if (data.accessToken) {
          dispatch(setToken({ value: data.accessToken }));
          return followCallbackAfterSignIn();
        }
      },
    );

  const doRespondNewPasswordChallenge = (newPassword: string) => {
    const session = sessionStorage.getItem(CHALLENGE_SESSION_KEY);
    const username = sessionStorage.getItem(CHALLENGE_USERNAME_KEY);
    const email = sessionStorage.getItem(CHALLENGE_EMAIL_KEY);
    
    if (!session || !email) {
      notification.error({ message: 'Session Expired', description: 'Please sign in again.' });
      gotoPage(SIGN_IN_PATH);
      return;
    }

    return handleApiCall(
      () =>
        callRespondChallengeApi({
          email,
          session,
          username: username || undefined,
          password: newPassword,
        }).unwrap(),
      (data) => {
        sessionStorage.removeItem(CHALLENGE_SESSION_KEY);
        sessionStorage.removeItem(CHALLENGE_USERNAME_KEY);
        sessionStorage.removeItem(CHALLENGE_EMAIL_KEY);

        if (data.accessToken) {
          dispatch(setToken({ value: data.accessToken }));
          return followCallbackAfterSignIn();
        } else if (data.challenge) {
          notification.error({
            message: 'Unsupported Login Challenge',
            description: `The system requested an unsupported challenge (${data.challenge}). Please contact support.`,
          });
          gotoPage(SIGN_IN_PATH);
        } else {
          notification.error({
            message: 'Unexpected Response',
            description: 'No access token received after challenge response. Please sign in again.',
          });
          gotoPage(SIGN_IN_PATH);
        }
      },
    );
  };

  const doSignupByEmail = (signUpParameter: SignUpByEmailPayload) =>
    handleApiCall(
      () => callSignUpApi(signUpParameter).unwrap(),
      (response: SignUpByEmailResponse) => {
        const { username } = response;
        dispatch(setSignUpFormPayload({ value: { ...signUpParameter, username } }));
        gotoPage(SIGN_UP_VERIFICATION_PATH);
      },
    );

  const doVerifySignUpOtp = (otp: string) =>
    handleApiCall(
      () =>
        callVerifySignUpOTPApi({
          username: state.username,
          password: state.password,
          code: otp,
        }).unwrap(),
      async (response: VerifySignUpOtpResponse) => {
        const { accessToken } = response;
        dispatch(setToken({ value: accessToken || null }));
        goAccountTypePage().then(() => {});
      },
    );

  const goAccountTypePage = async () => {
    await callSignupConfigApi().unwrap();
    const getQueryString: any = getQueryStringOfUrl(true);
    if (getQueryString['accountType']) {
      switch (getQueryString['accountType'].toLowerCase()) {
        case 'issuer':
          selectAccountType('Issuer' as AccountType);
          break;
        case 'investor':
          selectAccountType('Investor' as AccountType);
          break;
        case 'realtor':
          selectAccountType('Realtor' as AccountType);
          break;
        default:
          gotoPage(SIGN_UP_ACCOUNT_TYPE_PATH);
          break;
      }
    } else {
      gotoPage(SIGN_UP_ACCOUNT_TYPE_PATH);
    }
  };
  // Handles both completion screens: the first sign-up page and the "Create
  // account" modal. The two callbacks apply only in additional-account mode.
  const doCompleteSignUp = async (
    completeSignUpParameter: CompleteSignUpPayload,
    onAdditionalAccountCreated?: () => void,
    onAdditionalAccountFailed?: () => void,
  ) => {
    // One completion at a time in this session (completeSignUpFlow.ts). A
    // second submission, for example with the Enter key, does nothing.
    const attemptId = takeCompletionGuard(localStorage.getItem(AUTH_LOCAL_STORAGE_KEY));
    if (attemptId === null) {
      return;
    }
    // A confirmed recovery keeps the guard until the page leaves, including the
    // delayed redirect in handleCallBackInUrl. Every other result, and an
    // unexpected error, releases it.
    let leaving = false;
    try {
      errorsMeta.clearErrors();
      const body = {
        accountType: state.subAccountType || state.accountType,
        ...completeSignUpParameter,
      };
      const outcome = await submitCompleteSignUp(String(body.accountType), {
        readMe: () => callReadMeApi().unwrap(),
        completeSignUp: () => callCompleteSignUpApi(body).unwrap(),
        getSessionToken: () => localStorage.getItem(AUTH_LOCAL_STORAGE_KEY),
        now: () => new Date(),
      });

      switch (outcome.kind) {
        case 'account-not-loaded':
          errorsMeta.setErrors(ACCOUNT_NOT_LOADED_MESSAGE);
          return;
        case 'unresolved':
          dispatch(setSetupMarker({ value: outcome.marker }));
          return;
        case 'rejected':
          errorsMeta.handleApiError(outcome.error);
          return;
        case 'failed':
          errorsMeta.setErrors(ACCOUNT_NOT_CONFIRMED_MESSAGE);
          if (onAdditionalAccountFailed) {
            onAdditionalAccountFailed();
          }
          return;
        case 'session-changed':
          // A late response must not redirect or change the new session.
          return;
        case 'confirmed':
          // Additional-account mode keeps the active role and does not redirect.
          if (outcome.mode === 'additional-account') {
            if (onAdditionalAccountCreated) {
              onAdditionalAccountCreated();
            } else {
              notification.success({ message: ADDITIONAL_ACCOUNT_CREATED_MESSAGE });
            }
            return;
          }
          // Follow the callback only when a role is active. Otherwise the app
          // routing chooses the next step.
          if (!outcome.activeRoleId) {
            // The navigation drops the query string. Keep the URL callback for this
            // user: the app load activates a role, or shows the retry screen if the
            // account read fails, and follows the callback after a successful switch.
            const callbackUrl = readUrlCallback();
            if (callbackUrl && isHttpUrl(callbackUrl)) {
              savePendingCallback(outcome.username, callbackUrl);
            }
            window.location.href = '/';
            leaving = true;
            return;
          }
          handleCallBackInUrl(state.accountType === 'Issuer' ? '/l/apps/profile?p=issuer' : '/', outcome.username);
          leaving = true;
          return;
      }
    } finally {
      if (!leaving) {
        releaseCompletionGuard(attemptId);
      }
    }
  };

  const doForgetPassword = (forgetPasswordParameter: ForgotPasswordPayload) =>
    handleApiCall(
      () => callForgetPasswordApi(forgetPasswordParameter).unwrap(),
      (res) => {
        dispatch(setForgetPasswordFormPayload({ value: forgetPasswordParameter.email }));
        gotoPage(RESET_PASSWORD_PATH);
      },
    );

  const doResetPassword = (resetPasswordParameter: ResetPasswordPayload) =>
    handleApiCall(
      () =>
        callResetPasswordApi({
          email: state.email,
          password: resetPasswordParameter.password,
          code: resetPasswordParameter.code,
          confirmPassword: resetPasswordParameter.password,
        }).unwrap(),
      (res) => {
        notification.success({
          message: 'Password Changed!',
          description: 'You can now sign-in with your new password!',
        });
        setTimeout(() => {
          gotoPage(SIGN_IN_PATH);
        }, 200);
      },
    );

  const selectAccountType = (item: AccountType, withoutRedirect: boolean = false) => {
    dispatch(setAccountType({ value: item }));
    if (!withoutRedirect) {
      gotoPage(SIGN_UP_COMPLETE_PATH);
    }
  };

  const selectSubAccountType = (subAccountType: SubAccountType) => {
    dispatch(setSubAccountType({ value: subAccountType }));
  };

  const doSignOut = (redirect_url: string = SIGN_IN_PATH) => {
    dispatch(setToken({ value: null }));
    dispatch(setUser({ value: null }));
    dispatch(setRole({ value: null }));
    dispatch(setSetupMarker({ value: null }));
    localStorage.clear();
    removeCookie(AUTH_LOCAL_COOKIE_KEY);
    window.location.href = redirect_url;
  };

  // Waits for /v2/me before following the callback. A user without an active
  // role goes to the app instead, and the callback is kept for that user.
  const followCallbackAfterSignIn = async () => {
    const sessionToken = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
    let me: User | null = null;
    try {
      me = await callReadMeApi().unwrap();
    } catch {
      me = null;
    }
    // Another sign-in during the read handles its own redirect.
    if (localStorage.getItem(AUTH_LOCAL_STORAGE_KEY) !== sessionToken) {
      return;
    }
    if (!me) {
      // Keep the query string. The app shows a retry screen if the read still fails.
      windowRedirect('/');
      return;
    }
    // An unresolved setup replaces the callback redirect, even with an active
    // role. The app shows the unresolved screen.
    if (me.username && getSetupMarker(me.username)) {
      window.location.href = '/';
      return;
    }
    if (me.activeRoleId) {
      handleCallBackInUrl('/', me.username);
      return;
    }
    const callbackUrl = readUrlCallback();
    if (callbackUrl && me.username) {
      savePendingCallback(me.username, callbackUrl);
    }
    window.location.href = '/';
  };

  // With a username, a callback stored for that user is used when the URL has none.
  const handleCallBackInUrl = (url: string, username?: string) => {
    const pendingCallback = username ? takePendingCallback(username) : null;
    const callbackUrl = readUrlCallback() || pendingCallback;

    if (callbackUrl) {
      try {
        const parsedUrl = new URL(callbackUrl);
        // Validate the protocol to ensure it's http or https
        if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
          window.location.href = callbackUrl;
          return true;
        } else {
          console.warn('Blocked attempt to redirect to an unsafe protocol:', callbackUrl);
          window.location.href = url; // Redirect to a safe default
          return true;
        }
      } catch (error) {
        // URL parsing failed, likely an invalid or malicious URL
        console.warn('Blocked attempt to redirect to an invalid URL:', callbackUrl, error);
        window.location.href = url; // Redirect to a safe default
        return true;
      }
    } else {
      setTimeout(() => (window.location.href = url), 300);
    }
  };

  return {
    isUserAuthenticated: state.isUserAuthenticated,
    isLoading:
      isLoadingSignIn ||
      isSignUpByEmailLoading ||
      isVerifySignUpOtpLoading ||
      isLoadingSignupConfig ||
      isLoadingCompleteSignup ||
      isLoadingForgetPassword ||
      isGetRoleLoading ||
      isLoadingResetPassword ||
      isLoadingRespondChallenge ||
      isReadingMe,
    formInfo,
    doSignInByEmail,
    doSignupByEmail,
    doVerifySignUpOtp,
    doCompleteSignUp,
    doSignOut,
    selectAccountType,
    selectSubAccountType,
    error: errors,
    user: state.user,
    doForgetPassword,
    doSwitchRole,
    doResetPassword,
    handleCallBackInUrl,
    followCallbackAfterSignIn,
    goAccountTypePage,
    callSignupConfigApi,
    doRespondNewPasswordChallenge,
  };
};
